import React, { useState, useEffect } from 'react'

import { NumericFormat } from 'react-number-format';

import { IMaskInput } from "react-imask"

import InputMask from "react-input-mask"
import IntegerOnly from './IntegerOnly';

export const normalizeCnpj = (value = '') => String(value).toUpperCase().replace(/[^0-9A-Z]/g, '')

export const formatCnpj = (value = '') => {
    const cnpj = normalizeCnpj(value).slice(0, 14)
    let out = cnpj

    if (cnpj.length > 2) out = `${cnpj.slice(0, 2)}.${cnpj.slice(2)}`
    if (cnpj.length > 5) out = `${out.slice(0, 6)}.${out.slice(6)}`
    if (cnpj.length > 8) out = `${out.slice(0, 10)}/${out.slice(10)}`
    if (cnpj.length > 12) out = `${out.slice(0, 15)}-${out.slice(15)}`

    return out
}

export const isValidCnpj = (value) => {
    const rawCnpj = String(value || '').toUpperCase()

    if (/[^0-9A-Z.\-/\s]/.test(rawCnpj))
        return false

    const cnpj = normalizeCnpj(rawCnpj)

    if (cnpj.length !== 14)
        return false

    if (!/^[0-9A-Z]{12}[0-9]{2}$/.test(cnpj))
        return false

    if (/^([0-9A-Z])\1+$/.test(cnpj))
        return false

    const calcDigit = (base, weights) => {
        const sum = base
            .split('')
            .reduce((acc, char, index) => acc + (char.charCodeAt(0) - 48) * weights[index], 0)
        const mod = sum % 11
        return mod < 2 ? 0 : 11 - mod
    }

    const base = cnpj.slice(0, 12)
    const digit1 = calcDigit(base, [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2])
    const digit2 = calcDigit(`${base}${digit1}`, [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2])

    return cnpj === `${base}${digit1}${digit2}`
}

export const NumericFormatInput = React.forwardRef(function NumericFormatCustom(props, ref) {
    const { onChange, ...other } = props;
    const [key, setKey] = useState(Math.random())
    const keyHandled = React.useRef(false)

    const getValueInCents = () => {
        const val = other.value
        if (!val) return 0
        const num = parseFloat(val)
        if (isNaN(num)) return 0
        return Math.round(num * 100)
    }

    const handleKeyDown = (e) => {
        if (/^[0-9]$/.test(e.key)) {
            e.preventDefault()
            const newCents = getValueInCents() * 10 + parseInt(e.key, 10)
            if (other.max && newCents / 100 >= other.max) {
                setKey(Math.random())
                return
            }
            keyHandled.current = true
            onChange({ target: { name: props.name, value: (newCents / 100).toFixed(2) } })
        } else if (e.key === 'Backspace') {
            e.preventDefault()
            const newCents = Math.floor(getValueInCents() / 10)
            keyHandled.current = true
            onChange({ target: { name: props.name, value: (newCents / 100).toFixed(2) } })
        }
    }

    return (
        <NumericFormat
            key={`numeric-main-key-${key}`}
            {...other}
            getInputRef={ref}
            prefix={'R$ '}
            thousandSeparator="."
            decimalSeparator=","
            decimalScale={2}
            fixedDecimalScale
            allowNegative={false}
            valueIsNumericString
            onKeyDown={handleKeyDown}
            onValueChange={(values) => {
                if (keyHandled.current) {
                    keyHandled.current = false
                    return
                }
                const clamped = other.max ? String(IntegerOnly(String(Number(values.value) * 100), 0, other.max*100)/100) : values.value
                if (other.max && values.floatValue >= other.max)
                    setKey(Math.random())
                onChange({
                    target: {
                        name: props.name,
                        value: clamped || 0,
                    },
                });
            }}
        />
    )
})

export const CnpjMask = React.forwardRef(function TextMaskCustom(props, ref) {
    const { onChange, ...other } = props
    return (
        <IMaskInput
            {...other}
            mask="AA.AAA.AAA/AAAA-00"
            definitions={{
                A: /[A-Za-z0-9]/,
            }}
            inputRef={ref}
            prepare={(value) => value.toUpperCase()}
            onAccept={(value) => {
                if (normalizeCnpj(value) !== normalizeCnpj(props.value)) {
                    onChange({ target: { name: props.name, value: value.toUpperCase() } })
                }
            }}
            overwrite
        />
    )
})

export const TelephoneMask = React.forwardRef(function TextMaskCustom(props, ref) {
    const { onChange, ...other } = props
    return (
        <IMaskInput
            {...other}
            mask={[
                { mask: '(00) 0000-0000', overwrite: true }, 
                { mask: '(00) 00000-0000', overwrite: true }
            ]}
            definitions={{ "#": /[0-9]/ }}
            inputRef={ref}
            onAccept={(value) => {
                if (value !== props.value) {
                    onChange({ target: { name: props.name, value } })
                }
            }}
            overwrite
        />
    )
})

export const PostalCodeMask = React.forwardRef(function TextMaskCustom(props, ref) {
    const { onChange, ...other } = props
    return (
        <IMaskInput
            {...other}
            mask="00000-000"
            definitions={{ "#": /[0-9]/ }}
            inputRef={ref}
            onAccept={(value) => {
                if (value !== props.value) {  // Avoid unnecessary updates
                    onChange({ target: { name: props.name, value } })
                }
            }}
            overwrite
        />
    )
})

export const CpfMask = React.forwardRef((props, ref) => {
    const { onChange, ...other } = props;

    return (
        <InputMask
            {...other}
            mask="999.999.999-99"
            onChange={(e) => onChange(e)}
        >
            {(inputProps) => <input {...inputProps} ref={ref} />}
        </InputMask>
    )
})
